import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer, getCustomer } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Phone, Calendar } from 'lucide-react';

// Format phone for display: +18091234567 → (809) 123-4567
const formatPhone = (phone) => {
  if (!phone) return '';
  // Remove non-digits
  const digits = phone.replace(/\D/g, '');
  // Dominican Republic: +1 + 10 digits
  if (digits.length === 11 && digits.startsWith('1')) {
    const area = digits.slice(1, 4);
    const mid  = digits.slice(4, 7);
    const last = digits.slice(7);
    return `(${area}) ${mid}-${last}`;
  }
  // Other 10-digit numbers
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  // Return as-is for other formats
  return phone;
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(9);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showProfileHistory, setShowProfileHistory] = useState(false);
  const [phoneWarning, setPhoneWarning] = useState(null); // { message, data }
  const [formData, setFormData] = useState({
    name: '',
    last_name: '',
    phone: '',
    email: '',
    size: '',
    modifications: '',
    preferred_color: '',
    birthday: '',
    credit_limit: 0,
    has_logo: false,
    logo_notes: '',
    current_debt: 0
  });

  useEffect(() => { loadCustomers(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearching(true);
      loadCustomers(search, 1, perPage).finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadCustomers = async (q = '', targetPage = 1, targetPerPage = perPage) => {
    try {
      const params = new URLSearchParams({ page: targetPage, per_page: targetPerPage });
      if (q) params.set('q', q);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setCustomers(data.customers || []);
      setTotalPages(data.total_pages || 1);
      setTotalRecords(data.total || 0);
      setPage(targetPage);
      setPerPage(targetPerPage);
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
  };

  const handleSubmit = async (e, confirmDuplicate = false) => {
    if (e?.preventDefault) e.preventDefault();
    try {
      const payload = confirmDuplicate ? { ...formData, confirm_duplicate_phone: true } : formData;
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, payload);
        toast.success('Cliente actualizado');
      } else {
        await createCustomer(payload);
        toast.success('Cliente creado');
      }
      setIsDialogOpen(false);
      setPhoneWarning(null);
      resetForm();
      loadCustomers();
    } catch (error) {
      const res = error.response?.data;
      if (error.response?.status === 409 && res?.warning === 'duplicate_phone') {
        setPhoneWarning(res.message);
        return;
      }
      toast.error(res?.error || res?.detail || 'Error al guardar cliente');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '',
      last_name: customer.last_name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      size: customer.size || '',
      modifications: customer.modifications || '',
      preferred_color: customer.preferred_color || '',
      measurements: customer.measurements || {},
      birthday: customer.birthday || '',
      credit_limit: customer.credit_limit || 0,
      current_debt: customer.current_debt || 0,
      has_logo: customer.has_logo || false,
      logo_notes: customer.logo_notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este cliente?')) {
      try {
        await deleteCustomer(id);
        toast.success('Cliente eliminado');
        loadCustomers();
      } catch (error) {
        toast.error('Error al eliminar cliente');
      }
    }
  };

  const handleViewDetails = async (customerId) => {
    try {
      const customer = await getCustomer(customerId);
      setSelectedCustomer(customer);
      setShowHistory(false);
      setShowProfileHistory(false);
      setIsDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalles del cliente');
    }
  };

  const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2', '4', '6', '8', '10', '12', '14', '16'];
  const MEAS_KEYS = ['pecho', 'cintura', 'cadera', 'largo', 'manga'];
  const MEAS_LABELS = { pecho: 'Pecho', cintura: 'Cintura', cadera: 'Cadera', largo: 'Largo', manga: 'Manga' };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({ name:'', last_name:'', phone:'', email:'', size:'', modifications:'', preferred_color:'', birthday:'', credit_limit:0, current_debt:0, has_logo:false, logo_notes:'' });
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Clientes</h1>

        <div className="relative w-full md:w-80">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, teléfono o correo..."
            className="w-full border border-slate-200 rounded-lg px-4 py-2 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
          />
          <span className="absolute left-3 top-2.5 text-slate-400 text-sm">🔍</span>
          {searching && <span className="absolute right-3 top-2.5 text-slate-400 text-xs">...</span>}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-md shadow-cyan-500/20" data-testid="add-customer-button">
              <Plus className="w-5 h-5 mr-2" /> Nuevo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>
                {editingCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="customer-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Apellido *</Label>
                  <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required data-testid="customer-lastname-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono *</Label>
                  <div className="phone-input-container">
                    <PhoneInput
                      international
                      defaultCountry="DO"
                      value={formData.phone}
                      onChange={(value) => setFormData({ ...formData, phone: value || '' })}
                      data-testid="customer-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="cliente@ejemplo.com" data-testid="customer-email-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Talla</Label>
                <Select value={formData.size || ''} onValueChange={(v) => setFormData({ ...formData, size: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="customer-size-input"><SelectValue placeholder="Selecciona talla" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin talla —</SelectItem>
                    {SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-semibold">Medidas detalladas</Label>
                <div className="grid grid-cols-2 gap-2">
                  {MEAS_KEYS.map((k) => (
                    <div key={k} className="space-y-1">
                      <Label className="text-xs text-slate-500">{MEAS_LABELS[k]}</Label>
                      <Input value={(formData.measurements || {})[k] || ''}
                        onChange={(e) => setFormData({ ...formData, measurements: { ...(formData.measurements || {}), [k]: e.target.value } })}
                        placeholder='ej. 36"' className="h-8 text-sm" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Color Preferido</Label>
                <Input value={formData.preferred_color} onChange={(e) => setFormData({ ...formData, preferred_color: e.target.value })} data-testid="customer-color-input" />
              </div>
              <div className="space-y-2">
                <Label>Modificaciones</Label>
                <Input value={formData.modifications} onChange={(e) => setFormData({ ...formData, modifications: e.target.value })} data-testid="customer-modifications-input" />
              </div>
              <div className="space-y-2">
                <Label>Cumpleaños</Label>
                <Input type="date" value={formData.birthday} onChange={(e) => setFormData({ ...formData, birthday: e.target.value })} data-testid="customer-birthday-input" />
              </div>

              <div className="space-y-2">
                <Label>Logo del cliente</Label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, has_logo: !formData.has_logo })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.has_logo ? 'bg-emerald-500' : 'bg-slate-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.has_logo ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-sm text-slate-600">{formData.has_logo ? 'Tenemos el logo' : 'Sin logo registrado'}</span>
                </div>
                {formData.has_logo && (
                  <Input
                    placeholder="Notas del logo (formato, fecha entrega, etc.)"
                    value={formData.logo_notes}
                    onChange={(e) => setFormData({ ...formData, logo_notes: e.target.value })}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Límite de Crédito</Label>
                  <Input type="number" step="0.01" value={formData.credit_limit} onChange={(e) => setFormData({ ...formData, credit_limit: parseFloat(e.target.value) })} data-testid="customer-credit-limit-input" />
                </div>
                <div className="space-y-2">
                  <Label>Deuda Actual</Label>
                  <Input type="number" step="0.01" value={formData.current_debt} onChange={(e) => setFormData({ ...formData, current_debt: parseFloat(e.target.value) })} data-testid="customer-current-debt-input" />
                </div>
              </div>

              {phoneWarning && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="text-xl">⚠️</span>
                    <div>
                      <p className="font-semibold text-amber-900 text-sm">Teléfono duplicado</p>
                      <p className="text-sm text-amber-800 mt-0.5">{phoneWarning}</p>
                      <p className="text-xs text-amber-600 mt-1">¿Deseas continuar de todas formas?</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1 text-sm h-8" onClick={() => setPhoneWarning(null)}>
                      Cancelar
                    </Button>
                    <Button type="button" className="flex-1 text-sm h-8 bg-amber-500 hover:bg-amber-600 text-white"
                      onClick={() => { setPhoneWarning(null); handleSubmit(null, true); }}>
                      Continuar igual
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1" data-testid="save-customer-button">{editingCustomer ? 'Actualizar' : 'Crear'} Cliente</Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="cancel-customer-button">Cancelar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="customers-grid">
        {customers.map(customer => (
          <Card key={customer.id} className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
            onClick={() => handleViewDetails(customer.id)} data-testid={`customer-card-${customer.id}`}>
            <CardHeader>
              <CardTitle className="flex items-start justify-between" style={{ fontFamily: 'Outfit' }}>
                <div>
                  <h3 className="text-xl">{customer.name} {customer.last_name}</h3>
                  <p className="text-sm text-slate-500 font-normal flex items-center mt-1">
                    <Phone className="w-4 h-4 mr-1" />{formatPhone(customer.phone)}
                  </p>
                </div>
                <Users className="w-5 h-5 text-cyan-500" />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {customer.email && <div className="text-sm"><span className="text-slate-600">Email:</span> <span className="font-medium">{customer.email}</span></div>}
              {customer.size && <div className="text-sm"><span className="text-slate-600">Talla:</span> <span className="font-medium">{customer.size}</span></div>}
              {customer.preferred_color && <div className="text-sm"><span className="text-slate-600">Color:</span> <span className="font-medium">{customer.preferred_color}</span></div>}
              {customer.birthday && <div className="text-sm flex items-center"><Calendar className="w-4 h-4 mr-1 text-slate-500" /><span className="text-slate-600">{customer.birthday}</span></div>}
              {customer.current_debt > 0 && <div className="text-sm bg-rose-50 p-2 rounded"><span className="text-rose-600 font-bold">Deuda: ${customer.current_debt.toFixed(2)}</span></div>}

            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination controls */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-2">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span>Mostrar</span>
          <select value={perPage} onChange={(e) => { const v = Number(e.target.value); loadCustomers(search, 1, v); }}
            className="border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400">
            <option value={9}>9</option>
            <option value={18}>18</option>
          </select>
          <span>por página &nbsp;·&nbsp; {totalRecords} clientes en total</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => loadCustomers(search, 1, perPage)} disabled={page === 1}
            className="px-2 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50" title="Primera">«</button>
          <button onClick={() => loadCustomers(search, page - 1, perPage)} disabled={page === 1}
            className="px-3 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50">‹ Anterior</button>
          <span className="px-4 py-1.5 text-sm text-slate-600">
            Página <strong>{page}</strong> de <strong>{totalPages}</strong>
          </span>
          <button onClick={() => loadCustomers(search, page + 1, perPage)} disabled={page >= totalPages}
            className="px-3 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Siguiente ›</button>
          <button onClick={() => loadCustomers(search, totalPages, perPage)} disabled={page >= totalPages}
            className="px-2 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50" title="Última">»</button>
        </div>
      </div>

      {/* Customer Details Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Detalles del Cliente</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setIsDetailDialogOpen(false); handleEdit(selectedCustomer); }}
                  data-testid="detail-edit-button">
                  <Edit className="w-4 h-4 mr-2" />Editar
                </Button>
                <Button variant="outline" className="text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                  onClick={() => { setIsDetailDialogOpen(false); handleDelete(selectedCustomer.id); }}
                  data-testid="detail-delete-button">
                  <Trash2 className="w-4 h-4 mr-2" />Eliminar
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">Nombre Completo</p>
                  <p className="font-semibold">{selectedCustomer.name} {selectedCustomer.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Teléfono</p>
                  <p className="font-semibold">{formatPhone(selectedCustomer.phone)}</p>
                </div>
                {selectedCustomer.email && (
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-semibold">{selectedCustomer.email}</p>
                  </div>
                )}
                {Object.values(selectedCustomer.measurements || {}).some(v => v) && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-600 mb-1">Medidas</p>
                    <div className="grid grid-cols-3 gap-2">
                      {MEAS_KEYS.filter(k => selectedCustomer.measurements?.[k]).map(k => (
                        <div key={k} className="bg-slate-50 rounded px-2 py-1 text-sm">
                          <span className="text-slate-500 text-xs">{MEAS_LABELS[k]}: </span>
                          <span className="font-medium">{selectedCustomer.measurements[k]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedCustomer.size && <div><p className="text-sm text-slate-600">Talla</p><p className="font-semibold">{selectedCustomer.size}</p></div>}
                {selectedCustomer.preferred_color && <div><p className="text-sm text-slate-600">Color Preferido</p><p className="font-semibold">{selectedCustomer.preferred_color}</p></div>}
                {selectedCustomer.modifications && <div><p className="text-sm text-slate-600">Modificaciones</p><p className="font-semibold">{selectedCustomer.modifications}</p></div>}
              </div>

              {(selectedCustomer.profile_history || []).length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <button type="button" onClick={() => setShowProfileHistory((p) => !p)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={showProfileHistory} onCheckedChange={(v) => setShowProfileHistory(!!v)} onClick={(e) => e.stopPropagation()} className="data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500" />
                      <span className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>Historial de Cambios</span>
                      <span className="text-xs text-slate-400">({selectedCustomer.profile_history.length} registro{selectedCustomer.profile_history.length !== 1 ? 's' : ''})</span>
                    </div>
                    <span className="text-slate-400 text-xs">{showProfileHistory ? '▲ Ocultar' : '▼ Ver'}</span>
                  </button>
                  {showProfileHistory && (
                    <div className="p-4 space-y-4">
                      <div className="relative">
                        {selectedCustomer.profile_history.map((entry, idx) => (
                          <div key={idx} className="flex gap-3 pb-4 relative">
                            {idx < selectedCustomer.profile_history.length - 1 && <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-slate-200" />}
                            <div className="w-6 h-6 rounded-full bg-cyan-100 border-2 border-cyan-400 flex items-center justify-center shrink-0 mt-0.5">
                              <div className="w-2 h-2 rounded-full bg-cyan-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-slate-700">{new Date(entry.date).toLocaleDateString('es-DO', { day:'2-digit', month:'short', year:'numeric' })}</span>
                                {entry.order_code && <span className="text-xs bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">{entry.order_code}</span>}
                                {entry.updated_by && <span className="text-xs text-slate-400">por {entry.updated_by}</span>}
                              </div>
                              <div className="space-y-1">
                                {entry.changes.map((ch, ci) => (
                                  <div key={ci} className="flex items-center gap-2 text-xs bg-white border border-slate-100 rounded px-2 py-1">
                                    <span className="font-medium text-slate-600 w-24 shrink-0">{ch.label}:</span>
                                    <span className="text-red-400 line-through">{ch.from || '—'}</span>
                                    <span className="text-slate-400">→</span>
                                    <span className="text-green-600 font-medium">{ch.to}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedCustomer.purchase_history && selectedCustomer.purchase_history.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <button type="button" onClick={() => setShowHistory((p) => !p)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={showHistory} onCheckedChange={(v) => setShowHistory(!!v)} onClick={(e) => e.stopPropagation()} className="data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500" />
                      <span className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>Historial de Compras</span>
                      <span className="text-xs text-slate-400">({selectedCustomer.purchase_history.length} registro{selectedCustomer.purchase_history.length !== 1 ? 's' : ''})</span>
                    </div>
                    <span className="text-slate-400 text-xs">{showHistory ? '▲ Ocultar' : '▼ Ver'}</span>
                  </button>
                  {showHistory && (
                    <div className="divide-y divide-slate-100">
                      {selectedCustomer.purchase_history.map((invoice, index) => (
                        <div key={index} className="flex justify-between items-center px-4 py-3 hover:bg-slate-50">
                          <div>
                            <p className="font-semibold text-sm">{invoice.invoice_number}</p>
                            <p className="text-xs text-slate-500">{invoice.created_at}</p>
                          </div>
                          <p className="font-bold text-cyan-600 text-sm">RD${invoice.total.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>



      <style>{`
        .phone-input-container .PhoneInput {
          display: flex; align-items: center; height: 40px;
          border: 1px solid #e2e8f0; border-radius: 6px;
          background: white; padding: 0 12px; gap: 8px; font-size: 14px;
        }
        .phone-input-container .PhoneInput:focus-within {
          border-color: #06b6d4;
          box-shadow: 0 0 0 2px rgba(6,182,212,0.25);
        }
        .phone-input-container .PhoneInputInput {
          flex: 1; border: none; outline: none;
          background: transparent; font-size: 14px; color: #0f172a;
        }
        .phone-input-container .PhoneInputCountrySelect {
          border: none; background: transparent; cursor: pointer;
        }
        .phone-input-container .PhoneInputCountryIcon {
          width: 20px; height: 14px;
        }
      `}</style>
    </div>
  );
};

export default Customers;